/**
 * Config Registry API Service
 * API methods for Config Registry feature
 */

import { CMSApiResponse } from "framework/http/CMSApiClient";
import { RunContextApp } from "framework/RCApp";
import { API_TIMEOUT } from "constants/global";
import {
  ConfigRegistryItem,
  ConfigTypesResponse,
  UISchemaResponse,
  PaginationMeta,
  ConfigRegistryAPI as APITypes,
  Template,
  TemplateListResponse,
  TemplateDiffResult,
  TemplateUpdatePayload,
} from "../types/configRegistryTypes";

// Extended response type that includes meta from the API
// NOTE: CMSApiClient returns response.data which is the full API response { data, meta }
// So when we access response.data, we get the configs array, not { data, meta }
// The meta is at the same level in the raw response
//
// IMPORTANT: Config registry API returns FLAT meta structure (not nested under pagination)
// Backend returns: { data: [...], meta: { page, pageSize, pageCount, total, hasMore } }
export interface ConfigListApiResponse extends CMSApiResponse<ConfigRegistryItem[]> {
  meta?: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
    hasMore?: boolean;
  };
}

// =============================================================================
// API ENDPOINTS
// =============================================================================

const ENDPOINTS = {
  CONFIGS_LIST: '/config-registry/v1/configs',
  CONFIG_DETAIL: (id: string) => `/config-registry/v1/configs/${id}`,
  CONFIG_WITH_TEMPLATE: (id: string) => `/config-registry/v1/configs/${id}/with-template`,
  CONFIGS_CRUD: '/config-registry-configs',
  CONFIG_UPDATE: (id: string) => `/config-registry-configs/${id}`,
  CONFIG_TYPES: '/config-registry-configs/config-types',
  UI_SCHEMA: '/config-registry-lookup-types/v1/ui-schema',
  CREATE_LOOKUP: '/config-registry-lookups/v1/create',
  WORKFLOW_SUBMIT: (id: string) => `/config-registry-config/v1/${id}/submit`,
  WORKFLOW_APPROVE: (id: string) => `/config-registry-config/v1/${id}/approve`,
  WORKFLOW_REJECT: (id: string) => `/config-registry-config/v1/${id}/reject`,
  WORKFLOW_REOPEN: (id: string) => `/config-registry-config/v1/${id}/reopen`,
  VALIDATE: '/config-registry-configs/v1/validate',
  // Template endpoints (values live in their own table — no items payloads)
  TEMPLATE_CONFIGS_BY_NAME: (name: string) =>
    `/config-registry-templates/by-name/${encodeURIComponent(name)}/configs`,
  TEMPLATES_LIST: '/config-registry-template/v1/list',
  TEMPLATE_DETAIL: (id: string) => `/config-registry-templates/${id}`,
  TEMPLATE_VALUES: (id: string) => `/config-registry-template/v1/${id}/values`,
  TEMPLATE_VALUE_DELETE: (id: string, configId: string) =>
    `/config-registry-template/v1/${id}/values/${configId}`,
  TEMPLATE_CLONE: (id: string) => `/config-registry-template/v1/${id}/clone`,
  TEMPLATE_DIFF: '/config-registry-template/v1/diff',
};

// =============================================================================
// API SERVICE
// =============================================================================

export const ConfigRegistryApis = {
  /**
   * Get list of configs with pagination and filters
   * Note: API returns { data: [...], meta: { pagination: {...} } }
   * CMSApiClient interprets this as CMSApiResponse where data = the array
   */
  getConfigs: async (
    rc: RunContextApp,
    params: APITypes.ListParams = {}
  ): Promise<ConfigListApiResponse> => {
    const queryParams = new URLSearchParams();

    // Pagination
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    // Basic filters
    if (params.configType) queryParams.append('configType', params.configType);
    // Use verificationStatus if provided, otherwise fall back to approvalStatus for backward compatibility
    if (params.verificationStatus) queryParams.append('verificationStatus', params.verificationStatus);
    else if (params.approvalStatus) queryParams.append('verificationStatus', params.approvalStatus);
    if (params.expand) queryParams.append('expand', params.expand);
    if (params.search) queryParams.append('search', params.search);

    // Lookup type filters (documentIds)
    if (params.component) queryParams.append('component', params.component);
    if (params.section) queryParams.append('section', params.section);
    if (params.classification) queryParams.append('classification', params.classification);
    if (params.fileType) queryParams.append('fileType', params.fileType);
    if (params.restricted && params.restricted !== 'all') queryParams.append('restricted', params.restricted);
    if (params.excludeTemplate) queryParams.append('excludeTemplate', params.excludeTemplate);

    const queryString = queryParams.toString();
    const url = `${ENDPOINTS.CONFIGS_LIST}${queryString ? `?${queryString}` : ''}`;

    return rc.cmsApiClient.get<ConfigRegistryItem[]>(url) as Promise<ConfigListApiResponse>;
  },

  /**
   * Get single config by documentId
   */
  getConfigWithTemplate: async (
    rc: RunContextApp,
    documentId: string,
    templateName = 'GLOBAL'
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    const url = `${ENDPOINTS.CONFIG_WITH_TEMPLATE(documentId)}?templateName=${encodeURIComponent(templateName)}`;
    return rc.cmsApiClient.get<{ data: ConfigRegistryItem }>(url);
  },

  saveConfigWithTemplate: async (
    rc: RunContextApp,
    documentId: string,
    templateItems: { value?: string }[],
    templateName = 'GLOBAL'
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.put<{ data: ConfigRegistryItem }>(
      ENDPOINTS.CONFIG_WITH_TEMPLATE(documentId),
      { templateName, templateItems }
    );
  },

  getConfig: async (
    rc: RunContextApp,
    documentId: string,
    expand: boolean = true
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    const populateRelations = 'populate[parentConfig][fields][0]=configType&populate[parentConfig][fields][1]=configKey&populate[parentConfig][fields][2]=displayName&populate[relatedConfigs][fields][0]=configType&populate[relatedConfigs][fields][1]=configKey&populate[relatedConfigs][fields][2]=displayName&populate[duplicateConfig][fields][0]=configType&populate[duplicateConfig][fields][1]=configKey&populate[duplicateConfig][fields][2]=displayName&populate[configDependsUpon][fields][0]=configType&populate[configDependsUpon][fields][1]=configKey&populate[configDependsUpon][fields][2]=displayName';
    const url = expand
      ? `${ENDPOINTS.CONFIG_DETAIL(documentId)}?expand=full&${populateRelations}`
      : `${ENDPOINTS.CONFIG_DETAIL(documentId)}?${populateRelations}`;
    return rc.cmsApiClient.get<{ data: ConfigRegistryItem }>(url);
  },

  /**
   * Get list of config types for dropdown
   */
  getConfigTypes: async (
    rc: RunContextApp
  ): Promise<CMSApiResponse<ConfigTypesResponse>> => {
    return rc.cmsApiClient.get<ConfigTypesResponse>(ENDPOINTS.CONFIG_TYPES);
  },

  /**
   * Get UI schema for lookup dropdowns
   */
  getUISchema: async (
    rc: RunContextApp
  ): Promise<CMSApiResponse<UISchemaResponse>> => {
    return rc.cmsApiClient.get<UISchemaResponse>(ENDPOINTS.UI_SCHEMA);
  },

  /**
   * Create a new config
   */
  createConfig: async (
    rc: RunContextApp,
    data: APITypes.CreatePayload
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.CONFIGS_CRUD,
      { data }
    );
  },

  /**
   * Update an existing config
   * Uses PUT method as required by Strapi v5 for updates
   */
  updateConfig: async (
    rc: RunContextApp,
    documentId: string,
    data: Partial<APITypes.CreatePayload>
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.put<{ data: ConfigRegistryItem }>(
      ENDPOINTS.CONFIG_UPDATE(documentId),
      { data }
    );
  },

  /**
   * Submit config for approval (DRAFT -> READY_FOR_QA)
   */
  submitForApproval: async (
    rc: RunContextApp,
    documentId: string
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.WORKFLOW_SUBMIT(documentId),
      {}
    );
  },

  /**
   * Approve config (READY_FOR_QA -> APPROVED)
   */
  approveConfig: async (
    rc: RunContextApp,
    documentId: string
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.WORKFLOW_APPROVE(documentId),
      {}
    );
  },

  /**
   * Reject config (READY_FOR_QA -> REJECTED)
   */
  rejectConfig: async (
    rc: RunContextApp,
    documentId: string,
    reason?: string
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.WORKFLOW_REJECT(documentId),
      { reason }
    );
  },

  /**
   * Reopen config for editing (APPROVED -> TODO)
   */
  reopenConfig: async (
    rc: RunContextApp,
    documentId: string
  ): Promise<CMSApiResponse<{ data: ConfigRegistryItem }>> => {
    return rc.cmsApiClient.post<{ data: ConfigRegistryItem }>(
      ENDPOINTS.WORKFLOW_REOPEN(documentId),
      {}
    );
  },

  /**
   * Validate config data before saving
   */
  validateConfig: async (
    rc: RunContextApp,
    data: Partial<APITypes.CreatePayload>
  ): Promise<CMSApiResponse<{ valid: boolean; errors?: { field: string; message: string }[] }>> => {
    return rc.cmsApiClient.post<{ valid: boolean; errors?: { field: string; message: string }[] }>(
      ENDPOINTS.VALIDATE,
      data
    );
  },

  /**
   * Create a new lookup value
   */
  createLookup: async (
    rc: RunContextApp,
    data: {
      code: string;
      displayName: string;
      lookupType: string;
      parentLookup?: string;
      description?: string;
    }
  ): Promise<CMSApiResponse<{
    data: {
      documentId: string;
      code: string;
      displayName: string;
      description?: string;
      lookupType: { documentId: string; code: string; displayName: string };
      parentLookup?: { documentId: string; code: string; displayName: string } | null;
    };
    message: string;
  }>> => {
    return rc.cmsApiClient.post(ENDPOINTS.CREATE_LOOKUP, data);
  },

  // ===========================================================================
  // TEMPLATE APIs
  // ===========================================================================

  /**
   * GLOBAL template values keyed by config documentId — a config's default
   * value lives on the GLOBAL template, not on the config record.
   *
   * The configs list API returns metadata only (it doesn't populate
   * templateItems), so any "Default Value" column fed from that list needs this
   * one extra fetch. The strings side gets the equivalent data inlined by its
   * list API; configs don't, hence the explicit call.
   *
   * Fetch-all by design (no page params) — one request, lean populate.
   */
  getGlobalConfigValues: async (
    rc: RunContextApp,
    templateName = 'GLOBAL'
  ): Promise<{ error?: { message?: string }; values: Map<string, string> }> => {
    const response = await rc.cmsApiClient.get<any>(
      ENDPOINTS.TEMPLATE_CONFIGS_BY_NAME(templateName)
    );
    if ((response as any)?.error) {
      return { error: (response as any).error, values: new Map() };
    }
    // CMSApiClient returns the body verbatim; the payload has no { data } envelope
    const raw: any = response;
    const configs: any[] =
      raw?.fromTemplate?.configs ?? raw?.data?.fromTemplate?.configs ?? [];
    const values = new Map<string, string>();
    for (const config of configs) {
      if (config?.documentId && config.value != null && config.value !== '') {
        values.set(config.documentId, String(config.value));
      }
    }
    return { values };
  },

  /**
   * Get list of templates (with value counts)
   */
  getTemplates: async (
    rc: RunContextApp
  ): Promise<CMSApiResponse<TemplateListResponse>> => {
    return rc.cmsApiClient.get<TemplateListResponse>(ENDPOINTS.TEMPLATES_LIST);
  },

  /**
   * Get single template by documentId, adapted to the legacy Template shape
   * (items[] built from the template's value rows).
   */
  getTemplate: async (
    rc: RunContextApp,
    documentId: string
  ): Promise<CMSApiResponse<{ data: Template }>> => {
    const response = await rc.cmsApiClient.get<any>(ENDPOINTS.TEMPLATE_VALUES(documentId));
    if ((response as any)?.error) {
      return response as CMSApiResponse<{ data: Template }>;
    }
    // CMSApiClient returns the body verbatim; /values payload is at the top
    // level (no { data } envelope)
    const raw: any = (response as any)?.templateId ? response : (response as any)?.data;
    if (!raw?.templateId) {
      return { error: { message: 'Template not found' } } as unknown as CMSApiResponse<{ data: Template }>;
    }
    const template: Template = {
      id: 0,
      documentId: raw.templateId,
      name: raw.templateName,
      description: raw.description,
      module: raw.module,
      valueCount: raw.configCount,
      items: (raw.configs || []).map((c: any) => ({
        configRegistryConfig: {
          documentId: c.documentId,
          configKey: c.configKey,
          configType: c.configType,
          displayName: c.displayName,
          configDataType: c.configDataType,
        },
        value: c.value ?? null,
      })),
    };
    return { ...response, data: { data: template } } as CMSApiResponse<{ data: Template }>;
  },

  /**
   * Paginated template values with server-side search/filters — one fast page
   * per request instead of every populated row. Returns the raw payload:
   * { templateId, templateName, description, module, configCount, memberCount,
   *   configs[], facets: { configTypes, dataTypes }, memberConfigIds, pagination }
   */
  getTemplateValuesPage: async (
    rc: RunContextApp,
    documentId: string,
    params: {
      page?: number;
      pageSize?: number;
      search?: string;
      configType?: string;
      dataType?: string;
    } = {}
  ): Promise<{ error?: { message?: string }; payload?: any }> => {
    const q = new URLSearchParams();
    if (params.page) q.append('page', String(params.page));
    if (params.pageSize) q.append('pageSize', String(params.pageSize));
    if (params.search?.trim()) q.append('search', params.search.trim());
    if (params.configType) q.append('configType', params.configType);
    if (params.dataType) q.append('dataType', params.dataType);
    const qs = q.toString();
    const response = await rc.cmsApiClient.get<any>(
      `${ENDPOINTS.TEMPLATE_VALUES(documentId)}${qs ? `?${qs}` : ''}`
    );
    if ((response as any)?.error) {
      return { error: (response as any).error };
    }
    const raw: any = (response as any)?.templateId ? response : (response as any)?.data;
    if (!raw?.templateId) {
      return { error: { message: 'Template not found' } };
    }
    return { payload: raw };
  },

  /**
   * Update template header (name/description/module)
   */
  updateTemplate: async (
    rc: RunContextApp,
    documentId: string,
    data: TemplateUpdatePayload
  ): Promise<CMSApiResponse<{ data: Template }>> => {
    return rc.cmsApiClient.put<{ data: Template }>(
      ENDPOINTS.TEMPLATE_DETAIL(documentId),
      { data }
    );
  },

  /**
   * Upsert values into a template — sends only the changed rows
   */
  upsertTemplateValues: async (
    rc: RunContextApp,
    templateId: string,
    values: Array<{ configDocumentId: string; value: string | null }>
  ): Promise<CMSApiResponse<{ data: { created: number; updated: number; unchanged: number } }>> => {
    return rc.cmsApiClient.put(ENDPOINTS.TEMPLATE_VALUES(templateId), { values });
  },

  /**
   * Remove one config's value from a template
   */
  removeTemplateValue: async (
    rc: RunContextApp,
    templateId: string,
    configDocumentId: string
  ): Promise<CMSApiResponse<{ data: { deleted: boolean } }>> => {
    return rc.cmsApiClient.delete(ENDPOINTS.TEMPLATE_VALUE_DELETE(templateId, configDocumentId));
  },

  /**
   * Clone a template (header + every value row).
   *
   * Copies one value row per config, so a large template (GLOBAL is ~2400)
   * runs well past the 30s default and used to fail with
   * "timeout of 30000ms exceeded" after the server had already started writing.
   */
  cloneTemplate: async (
    rc: RunContextApp,
    documentId: string,
    name?: string,
    description?: string
  ): Promise<CMSApiResponse<{ data: Template; message: string }>> => {
    return rc.cmsApiClient.post<{ data: Template; message: string }>(
      ENDPOINTS.TEMPLATE_CLONE(documentId),
      { name, description },
      true,
      API_TIMEOUT.LONG_OPERATION
    );
  },

  /**
   * Compare two templates
   */
  diffTemplates: async (
    rc: RunContextApp,
    sourceId: string,
    targetId: string
  ): Promise<CMSApiResponse<TemplateDiffResult>> => {
    const url = `${ENDPOINTS.TEMPLATE_DIFF}?sourceId=${sourceId}&targetId=${targetId}`;
    return rc.cmsApiClient.get<TemplateDiffResult>(url);
  },
};

